import { createApp } from './vendor/petite-vue.min.js'
import gsap from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { useUrlQuery } from './modules/_query.js'

gsap.registerPlugin(ScrollToPlugin)

const PAGE_SIZE = 12
const DESKTOP_QUERY = '(min-width: 768px)' // 對應 scss 的 md 斷點
const PAGINATION_SLOTS = { mobile: 5, desktop: 7 } // 頁碼＋省略號固定顯示的格數，避免分頁列寬度跳動

// 依目前頁碼、總頁數、固定格數，產生頁碼陣列（格數永遠一致），例如 [1, '...', 4, 5, 6, '...', 99]
const getPageNumbers = (current, total, totalSlots) => {
	if (total <= totalSlots) {
		return Array.from({ length: total }, (_, i) => i + 1)
	}

	const edgeCount = totalSlots - 2 // 靠邊界時，含頭（或尾）固定顯示的頁碼數
	const middleCount = totalSlots - 4 // 中間（兩側都有省略號）時固定顯示的頁碼數

	if (current <= edgeCount) {
		return [...Array.from({ length: edgeCount }, (_, i) => i + 1), '...', total]
	}

	if (current >= total - edgeCount + 1) {
		return [1, '...', ...Array.from({ length: edgeCount }, (_, i) => total - edgeCount + 1 + i)]
	}

	const half = Math.floor(middleCount / 2)
	const middle = Array.from({ length: middleCount }, (_, i) => current - half + i)
	return [1, '...', ...middle, '...', total]
}

const useTabsOverflow = () => {
	const container = document.querySelector('.news__tabs')
	if (!container) return

	const update = () => {
		const { scrollLeft, scrollWidth, clientWidth } = container
		const hasLeft = scrollLeft > 1
		const hasRight = scrollLeft + clientWidth < scrollWidth - 1

		container.classList.toggle('is-overflow-left', hasLeft)
		container.classList.toggle('is-overflow-right', hasRight)
	}

	container.addEventListener('scroll', update, { passive: true })
	window.addEventListener('resize', update)
	update()
}

const useNewsApp = () => {
	const root = document.getElementById('app')
	if (!root) return

	const { updateMultipleQueries, getQuery } = useUrlQuery()

	// 直接從 SSR 已經渲染好的 DOM 讀取分類資料，不需要額外的 JSON
	const validTags = Array.from(document.querySelectorAll('.news__tab[data-tab]')).map(btn => btn.dataset.tab)
	const itemTags = Array.from(document.querySelectorAll('.news__item[data-category]')).map(el => el.dataset.category)

	const getInitTag = () => {
		const tag = getQuery('tag') || ''
		return validTags.includes(tag) ? tag : ''
	}

	createApp({
		isInit: false,
		isDesktop: window.matchMedia(DESKTOP_QUERY).matches,
		currentTag: getInitTag(),
		currentPage: Number(getQuery('page')) || 1,
		async setTag(id) {
			await this.updateWithTransition(() => {
				this.currentTag = id
				this.currentPage = 1
				updateMultipleQueries({ tag: this.currentTag || null, page: null })
			})
		},
		async setPage(page) {
			const target = Math.min(Math.max(1, page), this.totalPages)
			if (target === this.currentPage) return

			await this.updateWithTransition(() => {
				this.currentPage = target
				updateMultipleQueries({ page: target > 1 ? target : null })
			})
			this.scrollToList()
		},
		// 每篇文章對應「在目前篩選條件下排第幾個」，不符合篩選則為 -1
		get matchedRanks() {
			let rank = 0
			return itemTags.map(tag => {
				const matches = !this.currentTag || tag === this.currentTag
				return matches ? rank++ : -1
			})
		},
		get totalMatches() {
			return this.matchedRanks.filter(rank => rank !== -1).length
		},
		get totalPages() {
			return Math.ceil(this.totalMatches / PAGE_SIZE) || 1
		},
		get totalSlots() {
			return this.isDesktop ? PAGINATION_SLOTS.desktop : PAGINATION_SLOTS.mobile
		},
		get pageNumbers() {
			return getPageNumbers(this.currentPage, this.totalPages, this.totalSlots)
		},
		isVisible(idx) {
			const rank = this.matchedRanks[idx]
			if (rank === -1) return false

			const start = (this.currentPage - 1) * PAGE_SIZE
			return rank >= start && rank < start + PAGE_SIZE
		},
		scrollToList() {
			const offsetY = document.querySelector('.header')?.getBoundingClientRect().height || 80
			gsap.to(window, {
				duration: 1,
				ease: 'power4.inOut',
				scrollTo: {
					y: '.news__tabs',
					offsetY,
				},
			})
		},
		async updateWithTransition(updateFn) {
			if (!document.startViewTransition) {
				return updateFn()
			}

			const transition = document.startViewTransition(async () => {
				updateFn()
			})

			return transition.finished
		},
		onMounted() {
			this.isInit = true
			// 網址帶入的頁碼可能超出篩選後的實際頁數，掛載後修正回合法範圍
			this.currentPage = Math.min(Math.max(1, this.currentPage), this.totalPages)

			// 瀏覽器上一頁/下一頁時，還原篩選與分頁狀態
			window.addEventListener('query-change', (event) => {
				const { updates } = event.detail
				if ('tag' in updates) this.currentTag = validTags.includes(updates.tag) ? updates.tag : ''
				if ('page' in updates) this.currentPage = Number(updates.page) || 1
			})

			// 手機/桌面切換斷點時，分頁固定格數也要跟著切換
			window.matchMedia(DESKTOP_QUERY).addEventListener('change', (event) => {
				this.isDesktop = event.matches
			})

			if (getQuery('tag') || getQuery('page')) {
				this.scrollToList()
			}
		}
	}).mount('#app')
}

document.addEventListener('DOMContentLoaded', () => {
	useNewsApp()
	useTabsOverflow()
})
